import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DeleteWarning, Loading, SystemText } from '@salesperformanceint/e4shared';
import { apiGet, apiPatch, apiPost } from 'services';
import { e4Format, e4Formats, endOfDay, isAfter, isPast } from 'helpers/dates';
import Wizard from 'views/user/appraisals/components/wizard';
import { ReviewState } from 'views/user/appraisals';
import EditAppraisal from 'views/user/appraisals/components/editAppraisal';
import Schedule from 'views/user/reviewTracker/components/schedule';
import NoResults from 'components/prism/noResults';
import Subheader from 'components/prism/subheader';
import Toggle from 'components/prism/toggle';
import { getPermissions } from 'helpers/permissions';
import { setShowSuccess } from 'actions/ui';
import posthog from 'posthog-js';
import Bullet from 'components/prism/bullet';
import { translateAppraisalJson } from 'helpers/translations';

export const Reviews = ({ profile, reviewId }) => {
    const dispatch = useDispatch();
    const { coaching, downline, user } = useSelector(state => state.user);
    const [activeAppraisal, setActiveAppraisal] = React.useState(null);
    const [appraisals, setAppraisals] = React.useState(null);
    const [congrats, setCongrats] = React.useState(false);
    const [inReview, setInReview] = React.useState(false);
    const [permissions, setPermissions] = React.useState(getPermissions());
    const [showReset, setShowReset] = React.useState(false);
    const [showLoading, setShowLoading] = React.useState(true);
    const [showSchedule, setShowSchedule] = React.useState(false);
    const [stage, setStage] = React.useState(-1);
    const [state, setState] = React.useState({});
    const [selectedReview, setSelectedReview] = React.useState(null);
    const [tab, setTab] = React.useState('scheduled');
    const [showNudge, setShowNudge] = React.useState(false);
    const [showUnassign, setShowUnassign] = React.useState(false);
    const [reviewToAction, setReviewToAction] = React.useState(null);
    const [wizardKey, setWizardKey] = React.useState(0);

    const [showSummary, setShowSummary] = React.useState(false);

    const [reviewIdToView, setReviewIdToView] = React.useState(reviewId);

    const text = {
        tabs: {
            scheduled: SystemText('TeamMember/Reviews/Scheduled'),
            completed: SystemText('TeamMember/Reviews/Completed'),
        },
        buttons: {
            scheduleReview: SystemText('TeamMember/Reviews/Buttons/ScheduleReview'),
            startReview: SystemText('TeamMember/Reviews/Buttons/StartReview'),
            edit: SystemText('TeamMember/Reviews/Buttons/Edit'),
            nudge: SystemText('TeamMember/Reviews/Buttons/Nudge'),
            unassign: SystemText('TeamMember/Reviews/Buttons/Unassign'),
            summary: SystemText('TeamMember/Reviews/Buttons/ViewSummary'),
            backToReviews: SystemText('TeamMember/Reviews/Buttons/BackToReviews', profile?.first_Name || ''),
        },
        noResults: {
            noneScheduledTitle: SystemText('TeamMember/Reviews/Title/NoneScheduled', profile?.first_Name || ''),
            noneScheduledMessage: SystemText('TeamMember/Reviews/Message/NoneScheduled', profile?.first_Name || ''),
            noneCompletedTitle: SystemText('TeamMember/Reviews/Title/NoneCompleted', profile?.first_Name || ''),
            noneCompletedMessage: SystemText('TeamMember/Reviews/Message/NoneCompleted', profile?.first_Name || ''),
        },
        table: {
            scheduled: SystemText('TeamMember/Reviews/Table/Scheduled'),
            capabilities: SystemText('TeamMember/Reviews/Table/Capabilities'),
            assignedBy: SystemText('TeamMember/Reviews/Table/AssignedBy'),
            dueDate: SystemText('TeamMember/Reviews/Table/DueDate'),
            status: SystemText('TeamMember/Reviews/Table/Status'),
            completed: SystemText('TeamMember/Reviews/Table/Completed'),
        },
        status: {
            late: SystemText('TeamMember/Reviews/Status/Late'),
            onTime: SystemText('TeamMember/Reviews/Status/OnTime'),
            overdue: SystemText('TeamMember/Reviews/Status/Overdue'),
            outstanding: SystemText('TeamMember/Reviews/Status/Outstanding'),
        },
        success: {
            nudge: SystemText('TeamMember/Reviews/Nudge/Success'),
            unassign: SystemText('TeamMember/Reviews/Unassign/Success'),
        },
        warnings: {
            nudge: SystemText('TeamMember/Reviews/Nudge/Warning'),
            unassign: SystemText('TeamMember/Reviews/Unassign/Warning'),
        },
        na: SystemText('ReviewTracker/Report/NA'),
    };

    React.useEffect(() => {
        if (profile?.id && user?.id) {
            profile.active = true;
            setPermissions(getPermissions(profile, user, coaching, downline));
        }
    }, [profile?.id, user?.id]);

    const api = {
        loadAppraisals: callback => {
            setShowLoading(true);
            apiGet(`appraisals/${profile?.id}`, user.guid, response => {
                // Filter to only show appraisals whose launch date has passed
                const filteredResponse =
                    response?.filter(appraisal => {
                        // If no launch date, include the appraisal
                        if (!appraisal.launch) {
                            return true;
                        }
                        // Only include if launch date is in the past or is today
                        const launchDate = new Date(appraisal.launch);
                        const now = new Date();
                        return launchDate <= now;
                    }) || [];
                setAppraisals(filteredResponse);
                callback?.(filteredResponse);
                setShowLoading(false);
            });
        },
        loadProfile: () => profile,
        startAppraisal: () => {
            apiPatch(`appraisal-start/${profile?.id}`, activeAppraisal, user.guid, response => {
                posthog.capture('capability-review-started');
                api.loadAppraisals();
                setCongrats(false);
            });
        },
        updateAppraisal: (app, completed) => {
            const temp = { ...activeAppraisal };

            if (state.user) {
                temp.user_Predictions = app.user_Predictions;
                temp.user_Competency_Comments = app.user_Competency_Comments;
                temp.user_Comments = app.user_Comments;
                temp.user_Comments_Done = app.user_Comments_Done;
            } else {
                temp.manager_Predictions = app.manager_Predictions;
                temp.manager_Competency_Comments = app.manager_Competency_Comments;
                temp.manager_Comments = app.manager_Comments;
                temp.manager_Comments_Done = app.manager_Comments_Done;
            }

            temp.goals = app.goals;

            setActiveAppraisal(temp);

            apiPatch('appraisal-comment', app, user.guid, () => {
                completed && setCongrats(true);
            });
        },
        unassignReschedule: (app, callback) => {
            apiPost(`appraisal-unassign/${profile?.id}`, app, user.guid, response => {
                api.loadAppraisals(callback);
            });
        },
        assign: (app, callback) => {
            apiPost('review-assign', app, user.guid, response => {
                callback?.();
            });
        },
        nudge: confirm => {
            if (confirm && reviewToAction) {
                apiPatch('review-poke', [reviewToAction.id], user.guid, () => {
                    dispatch(setShowSuccess(text.success.nudge));
                    api.loadAppraisals();
                });
            }
            setShowNudge(false);
            setReviewToAction(null);
        },
        unassign: confirm => {
            if (confirm && reviewToAction) {
                apiPatch('review-unassign', [reviewToAction.id], user.guid, () => {
                    dispatch(setShowSuccess(text.success.unassign));
                    api.loadAppraisals();
                });
            }
            setShowUnassign(false);
            setReviewToAction(null);
        },
        loadFullReview: review => {
            apiGet(`appraisal-json/${review.id}`, user.guid, json => {
                const temp = { ...review };
                temp.json = JSON.stringify(json);
                temp.competencies = json.competencies || [];
                translateAppraisalJson(user.language_Code, temp);
                setReviewToAction(temp);
            });
        },
    };

    React.useEffect(() => {
        if (profile?.id) {
            api.loadAppraisals();
        }
    }, [profile?.id]);

    const showReview = review => {
        api.loadFullReview(review);
        const tempState = ReviewState(appraisals, review, user, profile, false, !!review?.submission);
        setState(tempState);
        setShowSummary(true);
    };

    const initialise = reset => {
        if (reset) {
            setActiveAppraisal(null);
            setAppraisals(null);
            setStage(-1);
            setState({});
            setSelectedReview(null);
            setReviewIdToView(null);
            api.loadAppraisals();
        } else {
            let reviewToShow;
            if (reviewIdToView) {
                reviewToShow = appraisals?.find(a => a.id === parseInt(reviewIdToView, 10));
                if (reviewToShow?.submission) {
                    setTab('completed');
                    showReview(reviewToShow);
                    return;
                } else {
                    setTab('scheduled');
                }
            } else {
                const active = appraisals?.find(a => !a.submission);

                // Use selectedReview if available, otherwise use the active review
                reviewToShow = selectedReview || active;
            }

            setActiveAppraisal(reviewToShow);
            const tempState = ReviewState(appraisals, reviewToShow, user, profile, false, !!selectedReview?.submission);

            setState(tempState);
        }
    };

    React.useEffect(() => {
        if (appraisals) {
            initialise();
        }
    }, [appraisals, selectedReview]);

    const setAgreedRatingValue = value => {
        const temp = { ...activeAppraisal };
        temp.agreed_Rating = value;
        setActiveAppraisal(temp);
    };

    const finalise = () => {
        apiPatch(`appraisal-finalise/${profile?.id}`, activeAppraisal, user.guid, response => {
            posthog.capture('capability-review-finalised');
            api.loadAppraisals(() => {
                setStage(-1);
                setCongrats(true);
            });
        });
    };

    const reset = () => {
        apiPatch(`appraisal-reset/${profile?.id}`, activeAppraisal, user.guid, response => {
            api.loadAppraisals(() => {
                setShowReset(false);
                setStage(-1);
            });
        });
    };

    const reviewStarted = profile?.id && tab === 'scheduled' && activeAppraisal && stage >= 0;

    const wizard = reviewStarted && (
        <Wizard
            key={wizardKey}
            appraisal={activeAppraisal}
            congrats={congrats}
            finalise={finalise}
            finalising={state.finalStarted && !state.past}
            inReview={inReview}
            permissions={permissions}
            profile={profile}
            setAgreedRatingValue={setAgreedRatingValue}
            setCongrats={setCongrats}
            setInReview={setInReview}
            setStage={s => {
                setStage(s);
                setCongrats(false);
            }}
            showBothManagerAndStaffRatingInfo={(state.finalStarted || state.past) && activeAppraisal?.participants === 'UM'}
            stage={stage}
            state={state}
            updateAppraisal={api.updateAppraisal}
            unassignReschedule={api.unassignReschedule}
        />
    );

    const summary = showSummary && (
        <>
            <button className="crumb-link mb20" onClick={() => setShowSummary(false)}>
                <i className="fa fa-arrow-left" />
                {text.buttons.backToReviews}
            </button>
            <Wizard
                key={wizardKey}
                usePrismSummary={true}
                appraisal={reviewToAction}
                congrats={congrats}
                finalising={false}
                inReview={false}
                permissions={permissions}
                profile={profile}
                setAgreedRatingValue={setAgreedRatingValue}
                setInReview={false}
                showBothManagerAndStaffRatingInfo={reviewToAction?.participants === 'UM'}
                stage={stage}
                state={state}
            />
            {reviewToAction?.competencies?.length > 10 && (
                <button className="crumb-link mt20" onClick={() => setShowSummary(false)}>
                    <i className="fa fa-arrow-left" />
                    {text.buttons.backToReviews}
                </button>
            )}
        </>
    );

    if (showLoading || !appraisals || !permissions.reviews.canView || !profile?.id) {
        return <Loading />;
    }

    // Filter scheduled reviews (no submission) and only include those with launch dates that have passed
    const scheduledReviews =
        appraisals?.filter(r => {
            // Only scheduled reviews (not completed)
            if (r.submission) return false;
            // If no launch date, include the review
            if (!r.launch) return true;
            // Only include if launch date is in the past or is today
            const launchDate = new Date(r.launch);
            const now = new Date();
            return launchDate <= now;
        }) || [];

    const completedReviews = appraisals?.filter(r => r.submission) || [];
    const displayedReviews = tab === 'scheduled' ? scheduledReviews : completedReviews;
    const displayedCount = tab === 'scheduled' ? scheduledReviews.length : completedReviews.length;

    // Helper functions for button visibility logic
    const shouldShowStartReviewButton = () => {
        if (!activeAppraisal) return false;

        const isManagerOrBothParticipants = activeAppraisal.participants === 'M' || activeAppraisal.participants === 'UM';
        const managerApprovalOutstanding = activeAppraisal.manager_Comments_Done == null;

        return isManagerOrBothParticipants && managerApprovalOutstanding;
    };

    const shouldShowScheduleReviewButton = () => {
        const noScheduledReviews = scheduledReviews.length === 0;

        return noScheduledReviews;
    };

    const actionButtonsscheduled = review => (
        <div className="flex flex-center" style={{ gap: '8px' }}>
            <button
                className="btn btn-cornered"
                title={text.buttons.edit}
                onClick={e => {
                    e.stopPropagation();
                    setActiveAppraisal(review);
                    setShowSchedule(true);
                }}
            >
                <i className="fa fa-pen" />
            </button>
            <button
                className="btn btn-cornered"
                title={text.buttons.nudge}
                onClick={e => {
                    e.stopPropagation();
                    setReviewToAction(review);
                    setShowNudge(true);
                }}
            >
                <i className="fa fa-triangle-exclamation" />
            </button>
            <button
                className="btn btn-cornered"
                title={text.buttons.unassign}
                onClick={e => {
                    e.stopPropagation();
                    setReviewToAction(review);
                    setShowUnassign(true);
                }}
            >
                <i className="fa fa-trash" />
            </button>
        </div>
    );

    const actionButtonsCompleted = review => (
        <div className="flex flex-center" style={{ gap: '8px' }}>
            <button
                className="btn btn-primary btn-cornered"
                onClick={e => {
                    e.stopPropagation();
                    showReview(review);
                }}
            >
                {text.buttons.summary}
            </button>
        </div>
    );

    return (
        <div className="mt20">
            {/* Reviews Table */}

            {!reviewStarted && !showSummary && (
                <>
                    <div className="mb20 flex flex-center">
                        <div className="flex-grow">
                            <Toggle value={tab} onChange={setTab}>
                                <button value="scheduled">{text.tabs.scheduled}</button>
                                <button value="completed">{text.tabs.completed}</button>
                            </Toggle>
                        </div>
                        <div className="flex" style={{ gap: '10px' }}>
                            {tab === 'scheduled' && shouldShowStartReviewButton() && (
                                <button
                                    className="btn btn-primary btn-cornered"
                                    onClick={() => {
                                        setInReview(false);
                                        setStage(0);
                                        setWizardKey(prev => prev + 1);
                                    }}
                                >
                                    {text.buttons.startReview}
                                </button>
                            )}
                            {tab === 'scheduled' && shouldShowScheduleReviewButton() && (
                                <button className="btn btn-primary btn-cornered" onClick={() => setShowSchedule(true)}>
                                    <i className="fa fa-calendar-plus mr10" />
                                    {text.buttons.scheduleReview}
                                </button>
                            )}
                        </div>
                    </div>

                    {displayedCount === 0 ? (
                        <NoResults
                            icon="gauge"
                            title={
                                tab === 'scheduled' ? text.noResults.noneScheduledTitle : text.noResults.noneCompletedTitle
                            }
                            summary={
                                tab === 'scheduled'
                                    ? text.noResults.noneScheduledMessage
                                    : text.noResults.noneCompletedMessage
                            }
                        />
                    ) : (
                        <>
                            <Subheader
                                title={tab === 'scheduled' ? text.tabs.scheduled : text.tabs.completed}
                                count={displayedCount}
                            />

                            <div className="mb20">
                                <table className="prism-table prism-table-reviews">
                                    <thead>
                                        <tr>
                                            <th>{text.table.scheduled}</th>
                                            <th>{text.table.capabilities}</th>
                                            <th>{text.table.assignedBy}</th>
                                            <th>{text.table.dueDate}</th>
                                            <th>{text.table.status}</th>
                                            {tab === 'completed' && <th>{text.table.completed}</th>}
                                            <th width="1"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayedReviews?.map((review, i) => {
                                            const isOverdue =
                                                review.scheduled && isPast(endOfDay(review.scheduled)) && !review.submission;
                                            const isLate =
                                                review.submission &&
                                                review.scheduled &&
                                                isAfter(review.submission, endOfDay(review.scheduled));
                                            const isSelected = selectedReview?.id === review.id;

                                            // Build manager name from first and last name
                                            const managerName =
                                                review.manager_First_Name && review.manager_Last_Name
                                                    ? `${review.manager_First_Name} ${review.manager_Last_Name}`
                                                    : review.manager_First_Name || review.manager_Last_Name || text.na;

                                            return (
                                                <tr
                                                    key={`review-${review.id}-${i}`}
                                                    className={isSelected ? 'active' : ''}
                                                    onClick={() => setSelectedReview(review)}
                                                >
                                                    <td>
                                                        {review.scheduled
                                                            ? e4Format(review.launch, e4Formats.date)
                                                            : text.na}
                                                    </td>
                                                    <td>{review.competency_Count}</td>
                                                    <td>{managerName}</td>
                                                    <td>
                                                        <Bullet isDate value={review.scheduled} />
                                                    </td>
                                                    <td>
                                                        {tab === 'scheduled' ? (
                                                            isOverdue ? (
                                                                <span className="label label-danger">
                                                                    <i className="fa fa-circle-exclamation mr5" />
                                                                    {text.status.overdue}
                                                                </span>
                                                            ) : (
                                                                <Bullet isStatus value={text.status.outstanding} />
                                                            )
                                                        ) : isLate ? (
                                                            <span className="label label-danger">
                                                                <i className="fa fa-circle-exclamation mr5" />
                                                                {text.status.late}
                                                            </span>
                                                        ) : (
                                                            <span className="label label-success">
                                                                <i className="fa fa-circle-check mr5" />
                                                                {text.status.onTime}
                                                            </span>
                                                        )}
                                                    </td>
                                                    {tab === 'completed' && (
                                                        <td>
                                                            <Bullet isDate value={review.submission} />
                                                        </td>
                                                    )}
                                                    <td>
                                                        {tab === 'scheduled'
                                                            ? actionButtonsscheduled(review)
                                                            : actionButtonsCompleted(review)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </>
            )}

            {summary}

            {wizard}

            <Schedule
                show={showSchedule && !activeAppraisal}
                users={[profile]}
                close={() => setShowSchedule(false)}
                onSchedule={app => {
                    setShowSchedule(false);
                    api.assign(app, api.loadAppraisals);
                }}
            />
            <EditAppraisal
                user={user}
                permissions={permissions}
                coaching={coaching}
                loadAppraisals={api.loadAppraisals}
                profile={profile}
                show={showSchedule && activeAppraisal}
                appraisal={activeAppraisal}
                reload={reset => {
                    initialise(reset);
                }}
                close={begin => {
                    setShowSchedule(null);
                    begin && setInReview(false);
                    begin && setStage(-1);
                }}
            />

            <DeleteWarning
                message={text.warnings.nudge}
                show={showNudge}
                mode="confirm"
                close={confirm => {
                    api.nudge(confirm);
                }}
            />

            <DeleteWarning
                message={text.warnings.unassign}
                show={showUnassign}
                mode="confirm"
                close={confirm => {
                    api.unassign(confirm);
                }}
            />
        </div>
    );
};

export default Reviews;
